import { Controller, useFormContext } from 'react-hook-form';
// @mui
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import Autocomplete, {
  AutocompleteProps,
  AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete';
import { Stack, CircularProgress } from '@mui/material';
import { isOptionEqualToValue as defaultIsOptionEqualToValue } from '@/utils/validation/autocomplete-helpers';
import { CopyText } from '@/components/utils/copy-text';

// ----------------------------------------------------------------------

interface Props<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  name: string;
  label?: string;
  placeholder?: string;
  renderCustomInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  helperText?: React.ReactNode;
  size?: 'small' | 'medium';
}

export default function RHFAutocomplete<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  name,
  label,
  placeholder,
  helperText,
  renderCustomInput,
  size = 'small',
  sx,
  ...other
}: Omit<Props<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'>) {
  const { control, setValue } = useFormContext();
  const theme = useTheme();

  // Default styling for RHF components
  const defaultSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'background.neutral',
      transition: 'all 0.2s ease-in-out',
      '& fieldset': {
        borderColor: 'divider',
        borderWidth: '1px',
      },
      '&.Mui-error': {
        backgroundColor: 'rgba(255, 72, 66, 0.04)',
        '& fieldset': {
          borderColor: '#ff6b6b',
          borderWidth: '1px',
        },
      },
      '&.Mui-disabled': {
        backgroundColor: 'action.hover',
        '& fieldset': {
          borderColor: 'action.disabled',
          borderStyle: 'dashed',
        },
        '& .MuiInputBase-input': {
          color: 'text.disabled',
          WebkitTextFillColor: 'unset',
        },
        '& .MuiAutocomplete-endAdornment': {
          '& .MuiSvgIcon-root': {
            color: 'action.disabled',
          },
        },
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '0.875rem',
      '&::placeholder': {
        color: 'text.disabled',
        opacity: 0.8,
      },
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      marginRight: 0,
      fontSize: '0.75rem',
      marginTop: theme.spacing(0.5),
      '&.Mui-error': {
        color: '#d32f2f',
        fontWeight: 400,
      },
      '&:not(.Mui-error)': {
        color: 'text.secondary',
        opacity: 0.8,
      },
    },
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          size={size}
          onChange={(event, newValue) => setValue(name, newValue, { shouldValidate: true })}
          getOptionDisabled={(option: any) => (option.status ? option.status !== 'active' : false)}
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') {
              return option;
            }
            if (option?.label) {
              return `${option.label} ${option.code ? `(${option.code})` : ''}`;
            }
            return option?.name || option?.title || '';
          }}
          isOptionEqualToValue={other.isOptionEqualToValue || defaultIsOptionEqualToValue}
          renderOption={(props, option: any) => {
            const { key, ...optionProps } = props;
            const { label: optionLabel } = option;

            return (
              <li key={key} {...optionProps}>
                <Stack direction="column" spacing={0.2}>
                  {optionLabel}
                  {option?.code && (
                    <CopyText text={option.code} sx={{ color: 'text.secondary', fontSize: 10.8 }} />
                  )}
                </Stack>
              </li>
            );
          }}
          {...other}
          renderInput={
            renderCustomInput ||
            (({ InputProps: inputProps, ...params }) => (
              <TextField
                label={label}
                placeholder={placeholder}
                error={!!error}
                helperText={error ? error?.message : helperText}
                // sx={sx ? { ...defaultSx, ...sx } : defaultSx}
                InputProps={{
                  ...inputProps,
                  endAdornment: (
                    <>
                      {other.loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {inputProps.endAdornment}
                    </>
                  ),
                }}
                {...params}
              />
            ))
          }
        />
      )}
    />
  );
}
